import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const HAMPTON_ID = "ad3d3d95-7da8-4b0c-a4aa-57031a873596";

function Row({ label, before, after }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs py-1 border-b border-white/5 last:border-0">
      <span className="text-white/50">{label}</span>
      <span className="text-white/70">{before}</span>
      <span className="text-[#F2B705] font-medium">{after}</span>
    </div>
  );
}

// Owner-only, narrowly scoped MONEY correction for Martrell Hampton — completely separate
// from the timeline-gap card above (different endpoints, different purpose). Dynamically
// re-scans for false duplicate $550 ledger rows every time it's opened/applied.
export default function HamptonPaymentLedgerFixCard({ bookingId, token, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [applied, setApplied] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  if (bookingId !== HAMPTON_ID) return null;

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/hampton-money-fix/preview`, auth);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the payment-ledger correction preview."); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    if (confirmText.trim().toUpperCase() !== "APPLY CORRECTION") { toast.error('Type "APPLY CORRECTION" exactly to confirm.'); return; }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/bookings/hampton-money-fix/apply`, { confirm_phrase: confirmText }, auth);
      setApplied(data);
      setConfirmText("");
      toast.success(data.already_applied ? "Already applied — no changes made." : "Payment ledger corrected.");
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply the correction."); }
    finally { setApplying(false); }
  };

  const done = preview?.already_applied || applied?.ok;

  return (
    <div data-testid={`hampton-payment-ledger-fix-card-${bookingId}`}
      className="rounded-lg border border-red-500/30 bg-[#0C0C0C] p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-400">
        <ShieldAlert size={14} /> Owner-Approved Correction — Martrell Hampton Payment Ledger
      </div>

      {!preview && (
        <button data-testid={`hampton-payment-ledger-fix-preview-btn-${bookingId}`} onClick={loadPreview} disabled={loading}
          className="text-xs font-semibold border border-red-500/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-500 hover:text-black transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="animate-spin inline" size={12} /> : "Correct Martrell Payment Ledger"}
        </button>
      )}

      {preview && (
        <div data-testid={`hampton-payment-ledger-fix-preview-${bookingId}`} className="space-y-2">
          {done ? (
            <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Already applied — Total $1,950.00, Late Fee Waiver $153.00, Paid $1,497.00, Balance Due $300.00, Credit $0.00.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-white/40 pb-1 border-b border-white/10">
                <span>Field</span><span>Current Live</span><span>Exact Target</span>
              </div>
              <Row label="Total Charges" before={money(preview.current_live.total_price)} after={money(preview.exact_target.total_price)} />
              <Row label="Late Fee Waiver (Discount)" before={money(preview.current_live.discount)} after={money(preview.exact_target.discount_waiver)} />
              <Row label="Legitimate Paid" before={money(preview.current_live.amount_paid)} after={money(preview.exact_target.amount_paid)} />
              <Row label="Balance Due" before="—" after={money(preview.exact_target.balance_due)} />
              <Row label="Credit" before="—" after={money(preview.exact_target.credit)} />
              <div className="text-[11px] text-white/50 pt-1">
                Detected <span className="text-red-300 font-semibold">{preview.duplicate_scan.num_duplicates_detected}</span> false
                duplicate $550.00 ledger row(s) to mark reversed (preserved, no refund). Balance due is exactly the
                still-unpaid current 3-day extension — no stray charge.
              </div>
              {!preview.can_apply_now && (
                <div data-testid={`hampton-payment-ledger-fix-blocked-${bookingId}`} className="flex items-center gap-2 text-amber-400 text-xs">
                  <AlertTriangle size={13} /> Live ledger doesn't cleanly resolve to the target yet — apply is disabled until it does.
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <input data-testid={`hampton-payment-ledger-fix-password-${bookingId}`} type="text" value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)} placeholder='Type "APPLY CORRECTION" to confirm'
                  className="bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white flex-1" />
                <button data-testid={`hampton-payment-ledger-fix-apply-btn-${bookingId}`} onClick={apply}
                  disabled={applying || !preview.can_apply_now}
                  className="text-xs font-semibold border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-50 whitespace-nowrap">
                  {applying ? <Loader2 className="animate-spin inline" size={12} /> : "Confirm & Apply"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
